import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeroSlide } from './components/HeroSlide';
import { InstructionSlide } from './components/InstructionSlide';
import { SpecialtyToggle } from './components/SpecialtyToggle';
import { QuestionSlide } from './components/QuestionSlide';
import { RewardSlide } from './components/RewardSlide';
import { PatternRecognition } from './components/PatternRecognition';
import { SegmentReveal } from './components/SegmentReveal';
import { CrossSpecialtyComparison } from './components/CrossSpecialtyComparison';
import { TreatmentJourney } from './components/TreatmentJourney';
import { DriversBarriers } from './components/DriversBarriers';
import { FinalCTA } from './components/FinalCTA';
import { SlideNavigation } from './components/SlideNavigation';
import { ErrorBoundary } from './components/ErrorBoundary';
type Specialty = 'cardiologist' | 'nephrologist' | 'gp' | 'endocrinologist';
function specialtyToKey(s: Specialty): string {
  if (s === 'gp') return 'GP';
  return s.charAt(0).toUpperCase() + s.slice(1);
}
const TOTAL_SLIDES = 14;
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
  const [visibleSpecialties, setVisibleSpecialties] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  // Initialize visibleSpecialties when specialty is selected
  useEffect(() => {
    if (selectedSpecialty) {
      setVisibleSpecialties([specialtyToKey(selectedSpecialty)]);
    }
  }, [selectedSpecialty]);
  const handleAnswer = (questionIndex: number, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
    setCurrentSlide(prev => prev + 1);
  };
  const handleSpecialtySelect = (specialty: Specialty) => {
    setSelectedSpecialty(specialty);
    setCurrentSlide(1);
  };
  const handleToggleSpecialty = (specKey: string) => {
    setVisibleSpecialties(prev => prev.includes(specKey) ? prev.filter(s => s !== specKey) : [...prev, specKey]);
  };
  const handleBack = () => {
    setCurrentSlide(prev => Math.max(0, prev - 1));
  };
  const handleNext = () => {
    setCurrentSlide(prev => Math.min(TOTAL_SLIDES - 1, prev + 1));
  };
  // Show toggle on reward slides (3, 5, 7) and comparison slides
  const isRewardSlide = currentSlide === 3 || currentSlide === 5 || currentSlide === 7;
  const showToggle = isRewardSlide && selectedSpecialty;
  const isQuestionSlide = currentSlide === 2 || currentSlide === 4 || currentSlide === 6;
  const hideNext = isQuestionSlide;
  return <div className="w-full min-h-screen bg-[#FAFAF8] relative overflow-hidden" data-id="element-0">
      {showToggle && <SpecialtyToggle visibleSpecialties={visibleSpecialties} onToggleSpecialty={handleToggleSpecialty} selectedSpecialty={selectedSpecialty} data-id="element-1" />}

      <SlideNavigation currentSlide={currentSlide} totalSlides={TOTAL_SLIDES} onBack={handleBack} onNext={handleNext} hideNext={hideNext} data-id="element-2" />

      <AnimatePresence data-id="element-3">
        <motion.div key={currentSlide} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="w-full min-h-screen" data-id="element-4">
          <ErrorBoundary data-id="element-5">
            {currentSlide === 0 && <HeroSlide onSelect={handleSpecialtySelect} data-id="element-6" />}

            {currentSlide === 1 && <InstructionSlide onContinue={() => setCurrentSlide(2)} data-id="element-7" />}

            {currentSlide === 2 && <QuestionSlide questionNumber={1} totalQuestions={3} question="What drives your treatment decision most?" options={['Long-term cardiovascular outcomes', 'Simplicity of treatment regimen', 'Patient adherence likelihood', 'Evidence strength and guidelines']} onAnswer={answer => handleAnswer(0, answer)} data-id="element-8" />}

            {currentSlide === 3 && <RewardSlide questionIndex={0} selectedAnswer={answers[0]} selectedSpecialty={selectedSpecialty!} visibleSpecialties={visibleSpecialties} onContinue={() => setCurrentSlide(4)} data-id="element-9" />}

            {currentSlide === 4 && <QuestionSlide questionNumber={2} totalQuestions={3} question="When choosing between two treatments with similar efficacy, what matters most?" subtitle="Think about your most recent prescribing decision" options={['Lower side effect profile', 'Dosing convenience', 'Cost and reimbursement', 'Novelty of mechanism']} onAnswer={answer => handleAnswer(1, answer)} data-id="element-10" />}

            {currentSlide === 5 && <RewardSlide questionIndex={1} selectedAnswer={answers[1]} selectedSpecialty={selectedSpecialty!} visibleSpecialties={visibleSpecialties} onContinue={() => setCurrentSlide(6)} data-id="element-11" />}

            {currentSlide === 6 && <QuestionSlide questionNumber={3} totalQuestions={3} question="What is your biggest barrier to prescribing newer therapies?" subtitle="Select the single biggest factor holding you back" options={['Uncertainty in long-term evidence', 'Patient insurance coverage', 'Lack of familiarity with mechanism', 'Preference for established options']} onAnswer={answer => handleAnswer(2, answer)} data-id="element-12" />}

            {currentSlide === 7 && <RewardSlide questionIndex={2} selectedAnswer={answers[2]} selectedSpecialty={selectedSpecialty!} visibleSpecialties={visibleSpecialties} onContinue={() => setCurrentSlide(8)} data-id="element-13" />}

            {currentSlide === 8 && <PatternRecognition answers={answers} onContinue={() => setCurrentSlide(9)} data-id="element-14" />}

            {currentSlide === 9 && <SegmentReveal answers={answers} selectedSpecialty={selectedSpecialty!} onContinue={() => setCurrentSlide(10)} data-id="element-15" />}

            {currentSlide === 10 && <CrossSpecialtyComparison viewMode="all" onContinue={() => setCurrentSlide(11)} data-id="element-16" />}

            {currentSlide === 11 && <TreatmentJourney segment="evidence-optimizer" onContinue={() => setCurrentSlide(12)} data-id="element-17" />}

            {currentSlide === 12 && <DriversBarriers segment="evidence-optimizer" selectedSpecialty={selectedSpecialty!} onContinue={() => setCurrentSlide(13)} data-id="element-18" />}

            {currentSlide === 13 && <FinalCTA data-id="element-19" />}
          </ErrorBoundary>
        </motion.div>
      </AnimatePresence>
    </div>;
}